/**
 * Buffered analytics event queue. Batches events and flushes them to
 * POST /api/events.
 *
 * Flush triggers:
 *   - buffer hits `FLUSH_SIZE` events
 *   - `FLUSH_INTERVAL_MS` has passed since the last flush
 *   - `visibilitychange → hidden` (best-effort, via fetch keepalive)
 *
 * Failure handling: if a flush fails, events go back in the buffer (head of
 * the line) and we try again on the next trigger. Buffer is capped at
 * `MAX_BUFFER` — older events are dropped beyond that. Best-effort; behavior
 * signal, not auditable truth.
 */
import type { UserEventInput } from '@portal/shared';
import { firebase } from '../firebase.js';

const FLUSH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 200;

export interface QueueAPI {
  enqueue(event: UserEventInput): void;
  /** Force a flush now (returns the promise so tests can await). */
  flush(): Promise<void>;
  destroy(): void;
}

async function postEvents(events: UserEventInput[], opts: { keepalive?: boolean } = {}): Promise<void> {
  if (events.length === 0) return;
  const { auth } = firebase();
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
  if (!token) {
    // No user — silently drop. Analytics shouldn't crash an unauthenticated page.
    return;
  }
  const res = await fetch('/api/events', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
    keepalive: opts.keepalive ?? false,
  });
  if (!res.ok) {
    throw new Error(`POST /api/events failed: ${res.status}`);
  }
}

export function startQueue(): QueueAPI {
  let buffer: UserEventInput[] = [];
  let inFlight = false;
  let lastFlushAt = Date.now();

  async function flushInternal(opts: { keepalive?: boolean } = {}): Promise<void> {
    if (buffer.length === 0 || inFlight) return;
    const batch = buffer.slice(0, FLUSH_SIZE);
    buffer = buffer.slice(batch.length);
    inFlight = true;
    try {
      await postEvents(batch, opts);
      lastFlushAt = Date.now();
    } catch {
      // Re-queue at the head; drop the oldest if we've blown the cap.
      buffer = [...batch, ...buffer].slice(-MAX_BUFFER);
    } finally {
      inFlight = false;
    }
  }

  const interval = window.setInterval(() => {
    if (buffer.length > 0 && Date.now() - lastFlushAt >= FLUSH_INTERVAL_MS) {
      void flushInternal();
    }
  }, 1000);

  const onVisibility = () => {
    if (document.visibilityState === 'hidden' && buffer.length > 0) {
      // Fire-and-forget. fetch with keepalive: true survives the page being
      // unloaded; sendBeacon would be alternative but can't carry an
      // Authorization header.
      void postEvents(buffer.splice(0, buffer.length), { keepalive: true });
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    enqueue(event) {
      buffer.push(event);
      if (buffer.length > MAX_BUFFER) {
        // Drop oldest beyond the cap.
        buffer.splice(0, buffer.length - MAX_BUFFER);
      }
      if (buffer.length >= FLUSH_SIZE) {
        void flushInternal();
      }
    },
    async flush() {
      await flushInternal();
    },
    destroy() {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

// Exposed for tests.
export const _internals = { FLUSH_SIZE, FLUSH_INTERVAL_MS, MAX_BUFFER };
